import { FC, ReactNode } from "react";
import { Button } from "@jobber/components/Button";
import { Content } from "@jobber/components/Content";
import { Flex } from "@jobber/components/Flex";
import { Heading } from "@jobber/components/Heading";
import { ProgressBar } from "@jobber/components/ProgressBar";
import { Spinner } from "@jobber/components/Spinner";
import { Text } from "@jobber/components/Text";
import { useBreakpoints } from "@jobber/hooks/useBreakpoints";
import { AuthFlowButton } from "../../types";

type AuthFlowProps = {
    readonly children: ReactNode | ReactNode[],
    pretitle: string,
    title: string,
    sidePanel?: ReactNode | ReactNode[],
    sidePanelContained?: boolean, // true if panel should be within content body
    step: number,
    totalSteps: number,
    button: AuthFlowButton,
    secondaryButton?: AuthFlowButton,
};

const AuthFlowFrame: FC<AuthFlowProps> = ({
    children,
    pretitle,
    title,
    sidePanel,
    sidePanelContained,
    step,
    totalSteps,
    button,
    secondaryButton,
}) => {
    let breakpoints = useBreakpoints();

    let heading =
        <Content spacing={"base"}>
            <Heading level={4}>{pretitle}</Heading>
            <Heading level={1}>{title}</Heading>
        </Content>;

    return (
        <div
            style={{
                width: "100%",
                padding: "40px",
                display: "flex",
                gap: "40px",
                flexDirection: "column",
                justifyContent: "space-between",
            }}
        >
            {sidePanelContained && heading}

            <div
                style={{
                    display: "flex",
                    gap: "40px",
                    flexDirection: breakpoints.mediumAndUp ? "row" : "column",
                    justifyContent: "space-between",
                }}
            >
                <Content spacing={"larger"}>
                    {!sidePanelContained && heading}
                    <div
                        style={{
                            maxWidth: "715px",
                            minHeight: breakpoints.mediumAndUp ? "317px" : "100%",
                        }}
                    >
                        {children}
                    </div>
                </Content>

                {sidePanel}
            </div>

            <Content spacing={"base"}>
                <div
                    id={"errors"}
                    style={{
                        display: "flex",
                        gap: "10px",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                ></div>

                <Flex
                    align={"end"}
                    gap={"small"}
                    template={["grow", "shrink"]}
                    direction={breakpoints.mediumAndUp ? "row" : "column"}
                >
                    <Content spacing={"smaller"}>
                        <Text size="base">Step <b>{step}</b> of <b>{totalSteps}</b></Text>
                        <div
                            style={{
                                maxWidth: breakpoints.mediumAndUp ? "375px" : "100%"
                            }}
                        >
                            <ProgressBar currentStep={step} totalSteps={totalSteps}/>
                        </div>
                    </Content>

                    <Flex
                        gap={"none"}
                        template={["grow", "shrink"]}
                    >
                        <div style={{
                            justifySelf: "flex-end",
                            marginLeft: "8px",
                        }}>
                            <Flex
                                gap={secondaryButton === undefined ? "none" : "small"}
                                template={["grow", "shrink"]}
                            >
                                {
                                    secondaryButton !== undefined &&
                                    <Button
                                        url={secondaryButton.link}
                                        label={secondaryButton.text}
                                        type={secondaryButton.type}
                                        disabled={secondaryButton.disabled}
                                        loading={secondaryButton.loading}
                                        onClick={secondaryButton.onClick}
                                    />
                                }
                                {
                                    button.link
                                        ? <Button
                                            url={button.link}
                                            label={button.text}
                                            type={button.type}
                                            disabled={button.disabled}
                                            loading={button.loading}
                                            onClick={button.onClick}
                                        />
                                        : <div style={{marginTop: "6px"}}>
                                            <Spinner size={"base"} inline={true} aria-label={"Loading"}/>
                                        </div>
                                }
                            </Flex>
                        </div>
                    </Flex>
                </Flex>
            </Content>
        </div>
    );
};

export default AuthFlowFrame;